import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
	FormControlLabel,
	Checkbox,
	Radio,
	RadioGroup,
	FormControl,
	CircularProgress,
	Avatar,
} from '@material-ui/core';
import SingleRoomStreaming from './SingleRoomStreaming';
import { getRooms as fetchRoomsApi } from '@/app/services/liveStreaming/liveStreaming';
import GradientButton from '@shared/components/GradientButton';
import PageHeader from '@shared/components/PageHeader/PageHeader';
import PDLogo from '@shared/icons/PDLogo';
import StatusPill from '@shared/components/StatusPill';

const GRID_GAP_PX = 2;
const SIDE_PANEL_PX = 360;
const BOTTOM_BAR_PX = 240;
const BASE_CANVAS_W = 1920;
const BASE_CANVAS_H = 1080;

// calculate optimal grid for screen size to maintain aspect ratio of streams
const computeOptimalGrid = (numTiles, containerWidth, containerHeight, gapPx) => {
	const aspectRatio = 16 / 9;
	let best = { cols: 1, rows: numTiles, tileW: 0, tileH: 0 };
	for (let cols = 1; cols <= numTiles; cols += 1) {
		const rows = Math.ceil(numTiles / cols);
		const availableWidth = containerWidth - gapPx * (cols - 1);
		const availableHeight = containerHeight - gapPx * (rows - 1);
		if (availableWidth <= 0 || availableHeight <= 0) continue;
		const maxTileWidth = Math.floor(availableWidth / cols);
		const maxTileHeight = Math.floor(availableHeight / rows);
		const tileW = Math.min(maxTileWidth, Math.floor(maxTileHeight * aspectRatio));
		const tileH = Math.floor(tileW / aspectRatio);
		if (tileW * tileH > best.tileW * best.tileH) best = { cols, rows, tileW, tileH };
	}
	return best;
};

const getPreviewLayout = (numTiles, orientation, previewWidth) => {
	const canvasW = BASE_CANVAS_W;
	const canvasH = BASE_CANVAS_H;
	const scale = previewWidth / canvasW;
	let gridW = canvasW;
	let gridH = canvasH;
	let sideW = 0;
	let bottomH = 0;
	if (orientation === 'bottom') {
		bottomH = BOTTOM_BAR_PX;
		gridH = canvasH - bottomH;
	} else {
		sideW = SIDE_PANEL_PX;
		gridW = canvasW - sideW;
	}
	const { cols, rows, tileW, tileH } = computeOptimalGrid(numTiles, gridW, gridH, GRID_GAP_PX);
	return {
		cols,
		rows,
		tileW: Math.max(6, Math.floor(tileW * scale)),
		tileH: Math.max(4, Math.floor(tileH * scale)),
		gap: Math.max(1, Math.floor(GRID_GAP_PX * scale)),
		sideW: Math.floor(sideW * scale),
		bottomH: Math.floor(bottomH * scale),
		previewW: previewWidth,
	};
};

const LobbyMultiviewFullscreen = ({
	rooms = [],
	onClose,
	brandingPosition = 'side', // 'side' | 'bottom'
}) => {
	const maxStreams = Math.min(12, Math.max(1, rooms.length || 0));
	const targetRooms = useMemo(() => rooms.slice(0, maxStreams), [rooms, maxStreams]);

	const streamsByRoomId = useMemo(() => {
		const map = {};
		(targetRooms || []).forEach((room) => {
			const su = room?.stream_url?.session_url || '';
			map[room.id] = su
				? {
						id: room.id,
						room: { id: room.id, name: room.name, thumb: room.thumb },
						thumb: room.thumb,
						overlay_image: room?.stream_url?.overlay_image || '',
						session_url: su,
						video_type: room?.stream_url?.video_type || 'hls',
					}
				: null;
		});
		return map;
	}, [targetRooms]);
	const loadingMap = useMemo(() => ({}), []);

	const MultiviewMarketingPanel = ({ orientation }) => {
		const isSide = orientation === 'side';
		const containerClass = `hidden lg:flex bg-white text-black p-6 ${
			isSide ? 'flex-col items-center justify-between' : 'w-full items-center justify-evenly'
		}`;
		const containerStyle = isSide ? { width: 316 } : { height: 240 };

		return (
			<div className={containerClass} style={containerStyle}>
				<div
					className={`flex relative w-full  ${isSide ? 'h-full flex-col items-center justify-evenly gap-8' : 'flex-row items-center justify-evenly gap-24'}`}
				>
					<PDLogo />
					<div className="text-center flex flex-col items-center justify-center text-[#2563EB]">
						<div className="font-bold text-36 max-w-[10ch] leading-[47px] text-center">Never Miss a Moment</div>
						<div className=" opacity-80 mt-2 text-center text-20 max-w-[20ch] leading-[34px]">
							Get instant photos, videos & updates from your child&apos;s day
						</div>
					</div>
					<div className="relative">
						<img
							src="assets/images/app-screenshot.jpg"
							alt="Scan to download"
							className={`relative shadow-xl ${!isSide ? 'translate-y-1/4' : ''} max-w-[180px] rounded-[28px]`}
						/>
					</div>
					<div className="border-2 rounded-lg p-2 border-pd-blue shadow-xl flex flex-col items-center justify-center">
						<img src="assets/images/pdl-qr.png" alt="Scan to download" style={{ width: 160, height: 160 }} />
						<span className="text-14 font-semibold text-pd-blue">Scan to stay connected!</span>
					</div>
				</div>
			</div>
		);
	};

	const gridContainerRef = useRef(null);
	const [gridSize, setGridSize] = useState({ w: 0, h: 0 });
	const gridGap = GRID_GAP_PX;

	useEffect(() => {
		if (!gridContainerRef.current) return undefined;
		const observer = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect;
			setGridSize({ w: Math.floor(width), h: Math.floor(height) });
		});
		observer.observe(gridContainerRef.current);
		return () => observer.disconnect();
	}, []);

	const optimalLayout = useMemo(() => {
		const totalTiles = Math.max(1, targetRooms.length);
		return computeOptimalGrid(totalTiles, gridSize.w, gridSize.h, gridGap);
	}, [gridSize.w, gridSize.h, targetRooms.length, gridGap]);

	const fullscreenRootRef = useRef(null);
	useEffect(() => {
		const el = fullscreenRootRef.current;
		if (!el) return undefined;

		const request =
			el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
		try {
			if (request && !document.fullscreenElement && !document.webkitFullscreenElement) {
				request.call(el);
			}
		} catch (_) {
			// noop
		}

		const handleFsChange = () => {
			const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
			if (!isFullscreen) {
				onClose?.();
			}
		};
		document.addEventListener('fullscreenchange', handleFsChange);
		document.addEventListener('webkitfullscreenchange', handleFsChange);
		return () => {
			document.removeEventListener('fullscreenchange', handleFsChange);
			document.removeEventListener('webkitfullscreenchange', handleFsChange);
		};
	}, [onClose]);

	const exitFullscreenAndClose = () => {
		const exit =
			document.exitFullscreen ||
			document.webkitExitFullscreen ||
			document.mozCancelFullScreen ||
			document.msExitFullscreen;
		try {
			if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) {
				exit.call(document);
			}
		} catch (_) {
			// noop
		}
		onClose?.();
	};

	return ReactDOM.createPortal(
		<div
			ref={fullscreenRootRef}
			className="fixed inset-0 z-[9999] bg-black text-white flex"
			style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
		>
			{brandingPosition === 'side' ? (
				<>
					{/* Side layout: grid left, marketing panel on the right */}
					<div ref={gridContainerRef} className="flex-1 w-full p-3 md:p-4 overflow-hidden">
						{(() => {
							const cols = Math.max(1, optimalLayout.cols);
							const rows = Math.ceil(Math.max(1, targetRooms.length) / cols);
							const gridPixelHeight = rows * optimalLayout.tileH + gridGap * (rows - 1) + gridGap * 2;
							const gridPixelWidth = cols * optimalLayout.tileW + gridGap * (cols - 1) + gridGap * 2;
							return (
								<div className="flex items-center justify-center w-full h-full">
									<div
										className="grid"
										style={{
											display: 'grid',
											gap: `${gridGap}px`,
											gridTemplateColumns: `repeat(${cols}, ${optimalLayout.tileW}px)`,
											gridAutoRows: `${optimalLayout.tileH}px`,
											width: gridPixelWidth,
											height: gridPixelHeight,
											padding: gridGap,

											borderRadius: 6,
										}}
									>
										{targetRooms.map((room) => {
											const streamData = streamsByRoomId[room.id];
											return (
												<div
													key={room.id}
													className="relative overflow-hidden bg-gray-950"
													style={{ width: `${optimalLayout.tileW}px`, height: `${optimalLayout.tileH}px` }}
												>
													{streamData ? (
														<SingleRoomStreaming streamData={streamData} isMultiview />
													) : (
														<div>
															<div className="flex items-center gap-8">
																<Avatar src={room.thumb} className="ml-10 mt-10" />
																<span className="text-14 font-bold mt-10">{room.name}</span>
															</div>
															<div className="absolute inset-0 flex items-center justify-center border border-gray-900">
																<div className="text-white text-16">
																	{loadingMap[room.id] ? 'Loading...' : 'Live Streaming Is Not Available'}
																</div>
															</div>
														</div>
													)}
												</div>
											);
										})}
									</div>
								</div>
							);
						})()}
					</div>
					<MultiviewMarketingPanel orientation="side" onExit={exitFullscreenAndClose} />
				</>
			) : null}
			{brandingPosition === 'bottom' ? (
				<div className="flex flex-col w-full h-full">
					<div ref={gridContainerRef} className="flex-1 w-full p-3 md:p-4 overflow-hidden">
						{(() => {
							const cols = Math.max(1, optimalLayout.cols);
							const rows = Math.ceil(Math.max(1, targetRooms.length) / cols);
							const gridPixelHeight = rows * optimalLayout.tileH + gridGap * (rows - 1) + gridGap * 2;
							const gridPixelWidth = cols * optimalLayout.tileW + gridGap * (cols - 1) + gridGap * 2;
							return (
								<div className="flex items-center justify-center w-full h-full">
									<div
										style={{
											display: 'grid',
											gap: `${gridGap}px`,
											gridTemplateColumns: `repeat(${cols}, ${optimalLayout.tileW}px)`,
											gridAutoRows: `${optimalLayout.tileH}px`,
											width: gridPixelWidth,
											height: gridPixelHeight,
											padding: gridGap,
											borderRadius: 6,
										}}
									>
										{targetRooms.map((room) => {
											const streamData = streamsByRoomId[room.id];
											return (
												<div
													key={room.id}
													className="relative overflow-hidden bg-black"
													style={{ width: `${optimalLayout.tileW}px`, height: `${optimalLayout.tileH}px` }}
												>
													{streamData ? (
														<SingleRoomStreaming streamData={streamData} isMultiview />
													) : (
														<div>
															<div className="flex items-center gap-8">
																<Avatar src={room.thumb} className="ml-10 mt-10" />
																<span className="text-14 font-bold mt-10">{room.name}</span>
															</div>
															<div className="absolute inset-0 flex items-center justify-center border border-gray-900">
																<div className="text-white text-16">
																	{loadingMap[room.id] ? 'Loading...' : 'Live Streaming Is Not Available'}
																</div>
															</div>
														</div>
													)}
												</div>
											);
										})}
									</div>
								</div>
							);
						})()}
					</div>
					<MultiviewMarketingPanel orientation="bottom" onExit={exitFullscreenAndClose} />
				</div>
			) : null}
		</div>,
		document.body
	);
};

const STORAGE_KEY = 'lobbyMultiview.selectedRoomIds';
const STORAGE_BRANDING = 'lobbyMultiview.brandingPosition';
const MAX_STREAMS = 12;
const MIN_STREAMS = 1;

const LobbyMultiview = ({ rooms = [], qrCodeUrl, appScreenshotUrl }) => {
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [roomsLoaded, setRoomsLoaded] = useState(false);
	const [brandingPosition, setBrandingPosition] = useState(() => {
		const saved = localStorage.getItem(STORAGE_BRANDING);
		return saved || 'side';
	});

	// Fetch real rooms from API (no mocked data)
	const [fetchedRooms, setFetchedRooms] = useState([]);

	useEffect(() => {
		let isMounted = true;
		const loadAllRooms = async (page = 1, acc = []) => {
			try {
				const res = await fetchRoomsApi('', '', page, 1);
				const next = [...acc, ...(res.data?.data || [])];
				if (res.data?.current_page < res.data?.last_page) {
					await loadAllRooms(page + 1, next);
				} else if (isMounted) {
					setFetchedRooms(next);
					setRoomsLoaded(true);
				}
			} catch (e) {
				console.error('Failed to fetch rooms', e);
			}
		};
		loadAllRooms();
		return () => {
			isMounted = false;
		};
	}, []);

	const allRooms = useMemo(() => {
		const base = rooms && rooms.length ? rooms : fetchedRooms;
		const normalized = (base || []).map((room) => ({
			...room,
			name: room?.name,
			id: room?.id,
			status: room?.stream_url?.session_url ? 'live' : 'offline',
		}));
		// move rooms with live streams to top of list
		return normalized.sort((a, b) => {
			const aLive = a.status === 'live' ? 1 : 0;
			const bLive = b.status === 'live' ? 1 : 0;
			if (aLive !== bLive) return bLive - aLive;
			return (a.name || '').localeCompare(b.name || '');
		});
	}, [rooms, fetchedRooms]);

	const [selectedRoomIds, setSelectedRoomIds] = useState(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			const parsed = raw ? JSON.parse(raw) : [];
			return parsed.slice(0, MAX_STREAMS);
		} catch (e) {
			return [];
		}
	});

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedRoomIds));
	}, [selectedRoomIds]);

	useEffect(() => {
		const valid = new Set(allRooms.map((r) => r.id));
		setSelectedRoomIds((prev) => {
			const filtered = prev.filter((id) => valid.has(id)).slice(0, MAX_STREAMS);
			const same = filtered.length === prev.length && filtered.every((v, i) => v === prev[i]);
			return same ? prev : filtered;
		});
	}, [allRooms]);

	const roomsForLaunch = useMemo(() => {
		const chosen = allRooms.filter((r) => selectedRoomIds.includes(r.id));
		return chosen.length ? chosen : allRooms.slice(0, Math.max(MIN_STREAMS, 1));
	}, [allRooms, selectedRoomIds]);

	const toggleSelect = (roomId) => {
		setSelectedRoomIds((prev) => {
			if (prev.includes(roomId)) return prev.filter((id) => id !== roomId);
			if (prev.length >= MAX_STREAMS) return prev;
			return [...prev, roomId];
		});
	};

	const clearSelections = () => {
		setSelectedRoomIds([]);
		localStorage.removeItem(STORAGE_KEY);
	};

	useEffect(() => {
		localStorage.setItem(STORAGE_BRANDING, brandingPosition);
	}, [brandingPosition]);

	const handleLaunchMultiview = () => {
		setIsFullscreen(true);
	};

	const handleCloseMultiview = () => {
		setIsFullscreen(false);
	};

	useEffect(() => {
		if (!isFullscreen) return;

		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				handleCloseMultiview();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isFullscreen]);

	if (isFullscreen) {
		return (
			<LobbyMultiviewFullscreen
				rooms={roomsForLaunch}
				onClose={handleCloseMultiview}
				qrCodeUrl={qrCodeUrl}
				appScreenshotUrl={appScreenshotUrl}
				brandingPosition={brandingPosition}
			/>
		);
	}

	const isLaunchDisabled = selectedRoomIds.length < MIN_STREAMS;

	return (
		<div className="px-40 mt-[30px] mb-128">
			<div className=" max-w-[1200px] mx-auto">
				<div className="pb-32">
					<PageHeader title="Perfect Day Live" subtitle="Lobby display configuration" hasBack={false} />
				</div>
				<div className="bg-white p-24 rounded-12">
					<div className=" bg-white flex flex-col md:flex-row gap-16 ">
						{/* Selection Section */}
						<div className="border p-16 rounded-12 w-full">
							<div className="flex items-center justify-between mb-8">
								<div className="flex items-center gap-8">
									<span className="font-bold text-16">Select Cameras for Lobby Display</span>
								</div>
								<div>
									<GradientButton onClick={clearSelections} size="small" variant="secondary">
										Clear All
									</GradientButton>
								</div>
							</div>
							<div className="text-14 text-gray-600 mb-16 flex items-center gap-8">
								<span>
									Choose {MIN_STREAMS}-{MAX_STREAMS} cameras to display in the lobby.{' '}
								</span>
								<StatusPill
									status={`Selected: ${selectedRoomIds.length}/${MAX_STREAMS}`}
									size="medium"
									variant="default"
									widthClass="px-12 py-2"
								/>
							</div>
							<div
								className={`${roomsLoaded ? 'flex flex-col md:grid md:grid-cols-2' : 'flex flex-col'} gap-12 min-h-[100px] max-h-[400px] overflow-y-auto roudned-md relative w-full`}
							>
								{roomsLoaded ? (
									allRooms.map((room) => {
										const isSelected = selectedRoomIds.includes(room.id);
										const isLive = room.status === 'live';
										return (
											<div
												role="button"
												tabIndex={0}
												key={room.id}
												onClick={() => toggleSelect(room.id)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														toggleSelect(room.id);
													}
												}}
												className={`cursor-pointer flex items-center justify-between rounded-md border px-8 py-0 text-left hover:bg-gray-50 transition ${
													isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
												}`}
											>
												<div className="flex items-center gap-8">
													<Checkbox
														color="primary"
														checked={isSelected}
														onChange={() => toggleSelect(room.id)}
														id={`room-${room.id}`}
													/>
													<label htmlFor={`room-${room.id}`} className="text-14 select-none">
														{room.name}
													</label>
												</div>
												<StatusPill
													status={isLive ? 'LIVE' : 'OFFLINE'}
													size="small"
													outlined={false}
													variant={isLive ? 'success' : 'default'}
												/>
											</div>
										);
									})
								) : (
									<div className="flex justify-center items-center h-full inset-0  z-99">
										<CircularProgress className="absolute top-0" />
									</div>
								)}
							</div>
						</div>

						{/* Layout options */}
						<div className="flex flex-col gap-16 justify-between">
							<div className="border p-16 rounded-12 w-full">
								<FormControl component="fieldset">
									<span className="text-16 font-bold">Layout Options</span>
									<span className="text-14 text-gray-500">Choose how to position the marketing elements</span>
									<RadioGroup
										aria-label="branding-position"
										name="branding-position"
										value={brandingPosition}
										onChange={(e) => setBrandingPosition(e.target.value)}
										className="mt-8"
									>
										<FormControlLabel value="side" control={<Radio color="primary" />} label="Right Side" />
										<FormControlLabel value="bottom" control={<Radio color="primary" />} label="Bottom" />
									</RadioGroup>
								</FormControl>
							</div>
							<div className="border p-16 rounded-12 w-full">
								<span className="text-16 font-bold">Preview Layout</span>
								{selectedRoomIds.length > 0 ? (
									<>
										<div className="mt-4 text-14 text-gray-500">
											{(() => {
												const n = Math.max(MIN_STREAMS, Math.min(MAX_STREAMS, selectedRoomIds.length || MIN_STREAMS));
												const { cols, rows } = getPreviewLayout(n, brandingPosition, 320);
												return (
													<span>
														{cols}×{rows} grid ({n} {n === 1 ? 'camera' : 'cameras'})
													</span>
												);
											})()}
										</div>
										<div className="mt-8">
											{(() => {
												const n = Math.max(MIN_STREAMS, Math.min(MAX_STREAMS, selectedRoomIds.length || MIN_STREAMS));
												const preview = getPreviewLayout(n, brandingPosition, 320);
												const { cols, rows, tileW, tileH, gap, sideW, bottomH, previewW } = preview;
												const gridPixelHeight = rows * tileH + gap * (rows - 1) + gap * 2;
												const gridPixelWidth = cols * tileW + gap * (cols - 1) + gap * 2;
												const cell = (index) => {
													const roomId = selectedRoomIds[index];
													const room = allRooms.find((r) => r.id === roomId);
													const roomName = room?.name || 'CAM';
													return (
														<div
															key={index}
															className="bg-blue-50 border border-blue-100 flex items-center justify-center  leading-none text-12 text-blue-700 p-2 text-center overflow-hidden"
															title={roomName} // Add tooltip for long names
														>
															{roomName}
														</div>
													);
												};

												if (brandingPosition === 'bottom') {
													return (
														<div className="w-full border" style={{ width: previewW }}>
															<div className="flex justify-center">
																<div
																	className="grid bg-gray-50"
																	style={{
																		display: 'grid',
																		gap: gap,
																		gridTemplateColumns: `repeat(${cols}, ${tileW}px)`,
																		gridAutoRows: `${tileH}px`,
																		padding: gap,
																	}}
																>
																	{Array.from({ length: n }).map((_, i) => cell(i))}
																</div>
															</div>
															<div
																className="mt-2 bg-blue-600 text-white flex items-center justify-evenly"
																style={{ height: bottomH }}
															>
																<div className="text-14">QR + App</div>
															</div>
														</div>
													);
												}

												// side
												return (
													<div className="w-full flex items-start gap-2 border p-1" style={{ width: previewW }}>
														<div className="flex items-start" style={{ width: previewW - sideW }}>
															<div
																className="grid rounded-md bg-gray-50"
																style={{
																	display: 'grid',
																	gap: gap,
																	gridTemplateColumns: `repeat(${cols}, ${tileW}px)`,
																	gridAutoRows: `${tileH}px`,
																	// padding: gap,
																	height: gridPixelHeight,
																	width: gridPixelWidth,
																	margin: '0 auto',
																}}
															>
																{Array.from({ length: n }).map((_, i) => cell(i))}
															</div>
														</div>
														<div
															className="bg-blue-600 text-white flex flex-col items-center justify-center text-14"
															style={{ width: sideW, height: gridPixelHeight }}
														>
															QR +
															<br />
															App
														</div>
													</div>
												);
											})()}
										</div>
									</>
								) : (
									(() => {
										const PREVIEW_W = 320;
										const PREVIEW_H = Math.floor((BASE_CANVAS_H / BASE_CANVAS_W) * PREVIEW_W);
										return (
											<div
												className="mt-8 flex items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-14 text-gray-500"
												style={{ width: PREVIEW_W, height: PREVIEW_H }}
											>
												Select a room to view preview
											</div>
										);
									})()
								)}
							</div>
							<div className="flex flex-col justify-end h-full bg-white">
								<div className={`mt-8 flex justify-end ${isLaunchDisabled ? 'pointer-events-none opacity-50' : ''}`}>
									<GradientButton
										onClick={handleLaunchMultiview}
										fullWidth
										disabled={isLaunchDisabled}
										variant={isLaunchDisabled ? 'tertiary' : 'blurple'}
									>
										Launch Lobby Display
									</GradientButton>
								</div>
							</div>
						</div>
					</div>
					<div className="w-full flex justify-center mt-16">
						<span className="text-gray-500 text-center w-full">Press ESC to exit multiview mode once launched.</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LobbyMultiview;
